import { useEffect, useMemo, useRef, useState } from 'react'
import ArenaSlot from '../components/ArenaSlot.jsx'
import Speaker from '../components/Speaker.jsx'
import { recordBattle } from '../lib/collection.js'
import { play } from '../lib/sounds.js'
import { track } from '../lib/analytics.js'
import { stopSpeaking } from '../lib/speech.js'
import { battleUrl, shareText } from '../lib/share.js'

/**
 * SPEC screen 6: winner victorious, loser retreating, confetti, the
 * "why they won" checklist ticking in one item at a time, then the loser's exit.
 *
 * Plus, at age 6, "Show me the numbers" (D-085): the six combat scores of both
 * animals, the weights, the totals and every rule that fired — the same
 * arithmetic `npm run matchup` prints, which is the answer to "why did the
 * badger win". And "Keep {winner}", because the next question is always
 * "OK, but could it beat the ELEPHANT?" (D-085).
 *
 * And "Share this battle" (D-145, docs/SHARE.md): a link to this matchup plus
 * one line of text, out through the OS share sheet — or, where there is no
 * sheet, copied to the clipboard with a "Link copied" on the button, or shown
 * as a selectable link. The text asks who wins and does not say: the recipient
 * gets to make the call (D-135). Hidden at age 2, because the sheet is the one
 * control in the app that reaches outside it (D-056).
 */
/** How long the button reads "Link copied" before it is a share button again. */
const COPIED_MS = 2000

// Each story object is recorded once. Effects can run twice (React StrictMode in
// dev, or a remount), and a battle must not count double.
const recorded = new WeakSet()

const DIMS = [
  ['power', 'Power'],
  ['weapons', 'Weapons'],
  ['armor', 'Armor'],
  ['speed', 'Speed'],
  ['smarts', 'Smarts'],
  ['ferocity', 'Ferocity'],
]

export default function Reveal({ story, numbers, A, B, age, call = null, features = null, onQuiz, onAgain, onKeep }) {
  const winner = story.winner === 'a' ? A : B
  const [ticked, setTicked] = useState(0)
  const [showNumbers, setShowNumbers] = useState(false)
  const numbersRef = useRef(null)
  // Sharing (D-145): 'copied' while the button says so, 'link' once the address
  // is shown on screen because neither the sheet nor the clipboard was there.
  const [shared, setShared] = useState(null)
  const copiedTimer = useRef(null)
  const urlRef = useRef(null)
  useEffect(() => () => clearTimeout(copiedTimer.current), [])
  // The shown address sits under the actions row, which on a phone is the bottom
  // of a scrolled screen — so it comes into view, the way the numbers panel does.
  useEffect(() => {
    if (shared !== 'link' || !urlRef.current) return
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    urlRef.current.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' })
  }, [shared])

  // The call (D-135). Scored into the collection at any age it was made; shown
  // only at age 6: a quiet tick when it was right, and when it was wrong the
  // numbers button takes the prominent spot under the title, because the
  // arithmetic is now the answer to something the kid actually said.
  const called = call === A.slug ? A : call === B.slug ? B : null
  const callRight = called ? called.slug === winner.slug : null
  const showCall = age === '6' && !!called
  const numbersUpTop = showCall && !callRight && !!numbers

  useEffect(() => {
    if (!recorded.has(story)) {
      recorded.add(story)
      // `numbers` is the decision the server sent (D-085); the surprise set
      // (D-136) is read off it, so a story with none can only be a surprise
      // through a wrong call.
      recordBattle({ a: A.slug, b: B.slug, winnerSlug: winner.slug, vocab: story.vocab_word, call, numbers })
      // Same once-per-story guard, so a battle is never counted twice here either.
      track('battle_complete', { a: A.slug, b: B.slug, winner: winner.slug, age })
    }
    play(winner.sound, { pose: 'victorious' })
    setTicked(0)
    setShowNumbers(false)
    setShared(null)
    clearTimeout(copiedTimer.current)
    const t = setInterval(() => setTicked((n) => (n >= story.why_won.length ? (clearInterval(t), n) : n + 1)), 750)
    return () => clearInterval(t)
  }, [story]) // eslint-disable-line react-hooks/exhaustive-deps

  // Deterministic confetti: same story, same shower.
  const pieces = useMemo(() => {
    const colors = ['#ffb020', '#4db4ff', '#3ddc84', '#ff5d5d', '#ffffff']
    let seed = story.title.length * 7919
    const rnd = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280)
    return Array.from({ length: 48 }, (_, n) => ({
      left: `${rnd() * 100}%`, delay: `${rnd() * 1.2}s`, dur: `${2.4 + rnd() * 1.8}s`,
      color: colors[n % colors.length], rot: `${rnd() * 360}deg`,
    }))
  }, [story.title])

  const spoken = `${story.reveal} ${showCall ? `Your call: ${called.name}. ` : ''}${story.why_won.join(' ')} ${story.loser_exit}`
  const done = ticked >= story.why_won.length

  // The panel opens in the why-column; on a phone that can be a screen below
  // the button that opened it, so bring it into view.
  const toggleNumbers = () => setShowNumbers((s) => !s)
  useEffect(() => {
    if (!showNumbers || !numbersRef.current) return
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    numbersRef.current.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' })
  }, [showNumbers])

  /**
   * Share this battle (D-145). Three paths, one handler, in order: the OS share
   * sheet; the clipboard, with the button saying "Link copied" for a moment;
   * the address itself, selectable, when neither API exists — which is exactly
   * the case on the iPad against `npm run dev` over plain http, where neither
   * is available, so a button that did nothing there would be the first thing
   * tried. The sheet is asked for synchronously inside the tap: on iOS a share
   * request that waits on anything first is refused as no longer the kid's
   * gesture. The button is never disabled and never shows a spinner.
   */
  const shareUrl = typeof window !== 'undefined' ? battleUrl({ a: A.slug, b: B.slug, age, origin: window.location.origin }) : ''
  const share = async () => {
    stopSpeaking() // the age-4 auto-read must not talk under the sheet
    const text = shareText(story.title)
    const payload = { title: 'ZooDuel', text, url: shareUrl }
    const done = (method, ok) => track('share_tap', { a: A.slug, b: B.slug, age, method, ok })
    if (typeof navigator.share === 'function' && (typeof navigator.canShare !== 'function' || navigator.canShare(payload))) {
      try {
        await navigator.share(payload)
        done('native', true)
        return
      } catch (err) {
        // The sender closed the sheet: nothing to show, nothing to log. Any
        // other failure falls through to the clipboard.
        if (err?.name === 'AbortError') {
          done('native', false)
          return
        }
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${shareUrl}`) // the whole line: the destination is a message
      setShared('copied')
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setShared((s) => (s === 'copied' ? null : s)), COPIED_MS)
      done('copy', true)
      return
    } catch {
      /* no clipboard either: show the address */
    }
    setShared('link')
    done('link', true)
  }

  const numbersButton = (primary) => (
    <button className={`btn ${primary ? 'btn--primary' : 'btn--small'} reveal__numbers-btn`} onClick={toggleNumbers} aria-expanded={showNumbers}>
      {showNumbers ? 'Hide the numbers' : 'Show me the numbers'}
    </button>
  )

  return (
    <section className="screen reveal">
      <div className="confetti" aria-hidden="true">
        {pieces.map((p, n) => <i key={n} style={{ left: p.left, background: p.color, animationDelay: p.delay, animationDuration: p.dur, transform: `rotate(${p.rot})` }} />)}
      </div>
      <div className="reveal__head">
        <h1 className="reveal__title">{story.reveal}</h1>
        {showCall && (
          <div className={`reveal__call${callRight ? ' is-right' : ''}`}>
            <span className="reveal__call-text">{callRight ? '✓ ' : ''}Your call: {called.name}</span>
            {numbersUpTop && numbersButton(true)}
          </div>
        )}
      </div>
      {/* VsScene, or with `arena_scene` on the arena, where the winner steps into the light (D-194). */}
      <ArenaSlot features={features} a={A} b={B} setting={story.setting} badge={null}
        poseA={story.winner === 'a' ? 'victorious' : 'retreating'}
        poseB={story.winner === 'b' ? 'victorious' : 'retreating'}
        cue={{ result: true, winner: story.winner }} />
      <div className="reveal__why">
        <h2>Why {winner.name} won <Speaker text={spoken} auto={age !== '6'} /></h2>
        <ul className="reveal__list">
          {story.why_won.map((w, n) => (
            <li key={n} className={`reveal__item${n < ticked ? ' is-in' : ''}`}>
              <span className="reveal__tick" aria-hidden="true">✓</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
        {done && <p className="reveal__exit">{story.loser_exit}</p>}
        {done && age === '6' && numbers && !numbersUpTop && numbersButton(false)}
      </div>
      {showNumbers && numbers && <Numbers numbers={numbers} A={A} B={B} ref={numbersRef} />}
      <div className="reveal__actions">
        <button className="btn" onClick={onAgain}>Battle again</button>
        <button className="btn" onClick={() => onKeep(winner.slug)}>Keep {winner.name}</button>
        <button className="btn btn--primary" onClick={onQuiz}>Quiz time!</button>
        {age !== '2' && (
          <button className={`btn reveal__share${shared === 'copied' ? ' is-copied' : ''}`} onClick={share}>
            {shared === 'copied' ? '✓ Link copied' : 'Share this battle'}
          </button>
        )}
      </div>
      {shared === 'copied' && <span className="visually-hidden" role="status">Link copied</span>}
      {shared === 'link' && (
        <div className="share__url" role="status" ref={urlRef}>
          <span className="share__url-label">Copy this link:</span>
          {/* A read-only field, not a link (SPEC 7: nothing navigates away). It selects itself so a long press copies the lot. */}
          <input className="share__url-field" type="text" readOnly value={shareUrl} onFocus={(e) => e.target.select()} onClick={(e) => e.target.select()} aria-label="Link to this battle" />
        </div>
      )}
    </section>
  )
}

/**
 * The arithmetic, laid out like a trading card: six rows of 1–10 scores with a
 * bar each, how much each row counts, the two totals, then every step of the
 * decision in the order the rules ran. Everything here comes from
 * matchup_rules.decide() on the server; nothing is computed on the client.
 */
function Numbers({ numbers, A, B, ref }) {
  const { a, b, weights, log } = numbers
  const pct = (w) => `${Math.round(w * 100)}%`
  const spoken = `How it was decided. ${log.join(' ')}`
  return (
    <div className="numbers" aria-label="How the winner was decided" ref={ref}>
      <table className="numbers__table">
        <thead>
          <tr>
            <th className="numbers__a">{A.name}</th>
            <th className="numbers__label">out of 10</th>
            <th className="numbers__b">{B.name}</th>
          </tr>
        </thead>
        <tbody>
          {DIMS.map(([k, label]) => {
            const va = a.combat[k]
            const vb = b.combat[k]
            const edge = va === vb ? '' : va > vb ? ' edge--a' : ' edge--b'
            return (
              <tr key={k} className={`numbers__row${edge}`}>
                <td className="numbers__a">
                  <span className="numbers__cell">
                    <span className="numbers__track numbers__track--a" aria-hidden="true"><i style={{ width: `${va * 10}%` }} /></span>
                    <span className="numbers__val">{va}</span>
                  </span>
                </td>
                <td className="numbers__label">{label}<small><span className="numbers__counts">counts </span>{pct(weights[k])}</small></td>
                <td className="numbers__b">
                  <span className="numbers__cell">
                    <span className="numbers__val">{vb}</span>
                    <span className="numbers__track numbers__track--b" aria-hidden="true"><i style={{ width: `${vb * 10}%` }} /></span>
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          {/* A rolled fight (D-157): the higher score is still the favourite's and keeps its colour; the row says the rest. */}
          <tr className={`numbers__row numbers__total${a.adjusted === b.adjusted ? '' : a.adjusted > b.adjusted ? ' edge--a' : ' edge--b'}${numbers.coin ? ' numbers__total--coin' : ''}`}>
            <td className="numbers__a"><span className="numbers__val">{a.adjusted}</span></td>
            <td className="numbers__label">Score{numbers.coin && <small>so close it could go either way</small>}</td>
            <td className="numbers__b"><span className="numbers__val">{b.adjusted}</span></td>
          </tr>
        </tfoot>
      </table>
      <h3 className="numbers__how">How it was decided <Speaker text={spoken} size="md" /></h3>
      <ol className="numbers__steps">
        {log.map((t, i) => <li key={i}>{t}</li>)}
      </ol>
    </div>
  )
}
